import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, signal } from '@angular/core';
import { Sparte, TextNodeType } from '@mwe/models';
import { isProductCategory, translateCategory } from '@mwe/utils';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { HeadlineService } from '../headline/headline.service';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../headline/headline.component';
import { CategoryIconSrcPipe } from '@mwe/ui/pipes';
import { ProfileService } from '@mwe/services';
import { FeatureToggleEnum, ProductCategoryEnum } from '@mwe/constants';

type ProductIconsSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

@Component({
  selector: 'mwe-product-icons',
  templateUrl: './product-icons.component.html',
  providers: [HeadlineService],
  imports: [NgClass, HeadlineComponent, CategoryIconSrcPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductIconsComponent implements OnInit {
  private translateService = inject(TranslateService);
  private headlineService = inject(HeadlineService);
  private readonly profileService = inject(ProfileService);

  categories = input<(Sparte | string)[]>([]);
  categoryTranslationKeys = model<Record<string, string>>({});
  size = input<ProductIconsSize>('md');
  textNodeType = input<TextNodeType>('h6');
  isDisabled = input<boolean>(false);
  alignment = input<'start' | 'center' | 'end'>('start');
  showHeadline = input<boolean>(true);
  maxWidth = computed<string>(() => {
    switch (this.size()) {
      case '2xs': {
        return '.75rem';
      }
      case 'xs': {
        return '1rem';
      }
      case 'sm': {
        return '1.25rem';
      }
      case 'md': {
        return '1.5rem';
      }
      case 'lg': {
        return '2rem';
      }
      case 'xl': {
        return '2.5rem';
      }
      case '2xl': {
        return '3.5rem';
      }
      case '3xl': {
        return '4rem';
      }
      case '4xl': {
        return '4.5rem';
      }
    }
  });
  fontSize = computed<string>(() => {
    switch (this.size()) {
      case '2xs': {
        return '.75rem';
      }
      case 'xs': {
        return '.75rem';
      }
      case 'sm': {
        return '.75rem';
      }
      case 'md': {
        return '1.125rem';
      }
      case 'lg': {
        return '1.125rem';
      }
      case 'xl': {
        return '1.125rem';
      }
      case '2xl': {
        return '1.125rem';
      }
      case '3xl': {
        return '1.125rem';
      }
      case '4xl': {
        return '1.125rem';
      }
    }
  });
  heading: string;
  translatedProductCategories = signal<string[]>([]);
  joinedLabels = computed<string>(() => {
    return this.translatedProductCategories().join(' & ');
  });
  rowClass = computed<string>(() => {
    switch (this.alignment()) {
      case 'start': {
        return 'justify-content-start';
      }
      case 'center': {
        return 'justify-content-center';
      }
      case 'end': {
        return 'justify-content-end';
      }
    }
  });
  textClass = computed<string>(() => {
    switch (this.alignment()) {
      case 'start': {
        return 'text-start';
      }
      case 'center': {
        return 'text-center';
      }
      case 'end': {
        return 'text-end';
      }
    }
  });

  // TEMP: Only used for internet product card until FT internet-v2-enabled is default enabled
  private readonly isNewInternetProcessEnabled = signal(false);

  async ngOnInit() {
    this.headlineService.setReferenceHeadline(this.textNodeType());
    this.isNewInternetProcessEnabled.set(this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.INTERNET_V2_ENABLED));
    this.transformCategories();
    const categoryTranslationKeys = this.categoryTranslationKeys();
    const translatedProductCategories = await Promise.all(
      this.categories()?.map(category => {
        const translationKey = categoryTranslationKeys[category];
        if (translationKey) {
          return firstValueFrom(this.translateService.get(translationKey));
        }

        return translateCategory(category, this.translateService);
      }),
    );
    this.translatedProductCategories.set(translatedProductCategories);
  }

  private transformCategories() {
    if (
      !this.isNewInternetProcessEnabled() ||
      !this.categories().some(category => isProductCategory(category, ProductCategoryEnum.INTERNET))
    ) {
      return;
    }

    // given key value pairs should always be the higher priority.
    this.categoryTranslationKeys.update(translations => ({
      [Sparte.Internet]: 'products.category.internet_new',
      ...translations,
    }));
  }
}
